import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { ApplicationUser, ProfileModel } from '@/lib/models/applicationUser';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per gli endpoints User
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
export interface UserEndpoints {
	/**
	 * Recupera lista paginata di users con query OData
	 * @param query - Query string OData
	 * @returns Promise con FetchResponse contenente ODataResponse<ApplicationUser>
	 */
	getUsers(query: string): Promise<FetchResponse<ODataResponse<ApplicationUser>>>;

	/**
	 * Recupera un singolo user per ID
	 * @param id - ID dell'utente
	 * @returns Promise con FetchResponse contenente ApplicationUser
	 */
	getUser(id: string): Promise<FetchResponse<ApplicationUser>>;

	/**
	 * Aggiorna un user esistente
	 * @param id - ID dell'utente da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con FetchResponse contenente ApplicationUser aggiornato
	 */
	updateUser(id: string, payload: ApplicationUser): Promise<FetchResponse<ApplicationUser>>;

	/**
	 * Crea un nuovo user
	 * @param payload - Dati del nuovo utente
	 * @returns Promise con FetchResponse contenente ApplicationUser creato
	 */
	postUser(payload: ApplicationUser): Promise<FetchResponse<ApplicationUser>>;

	/**
	 * Elimina un user
	 * @param id - ID dell'utente da eliminare
	 * @returns Promise con FetchResponse contenente void
	 */
	deleteUser(id: string): Promise<FetchResponse<void>>;

	/**
	 * Aggiunge un ruolo all'utente
	 * @param id - ID dell'utente
	 * @param roleId - ID del ruolo da aggiungere
	 * @returns Promise con FetchResponse contenente void
	 */
	addRoleToUser(id: string, roleId: string): Promise<FetchResponse<void>>;

	/**
	 * Rimuove un ruolo dall'utente
	 * @param id - ID dell'utente
	 * @param roleId - ID del ruolo da rimuovere
	 * @returns Promise con FetchResponse contenente void
	 */
	removeRoleFromUser(id: string, roleId: string): Promise<FetchResponse<void>>;

	/**
	 * Cambia lo stato attivo/inattivo dell'utente
	 * @param id - ID dell'utente
	 * @returns Promise con FetchResponse contenente void
	 */
	toggleEnablingStatus(id: string): Promise<FetchResponse<void>>;

	/**
	 * Recupera il ruolo per ID utente
	 * @param id - ID dell'utente
	 * @returns Promise con FetchResponse contenente string (primo ruolo dell'utente)
	 */
	getRoleByUserId(id: string): Promise<FetchResponse<string>>;

	/**
	 * Reset password utente
	 * @param id - ID dell'utente
	 * @param newPassword - Nuova password
	 * @returns Promise con FetchResponse contenente void
	 */
	resetPassword(id: string, newPassword: string): Promise<FetchResponse<void>>;

	/**
	 * Recupera il profilo dell'utente corrente
	 * @returns Promise con FetchResponse contenente ProfileModel
	 */
	getProfile(): Promise<FetchResponse<ProfileModel>>;

	/**
	 * Aggiorna il profilo dell'utente corrente
	 * @param payload - Dati del profilo da aggiornare (ProfileModel)
	 * @returns Promise con FetchResponse contenente ProfileModel
	 */
	updateProfile(payload: ProfileModel): Promise<FetchResponse<ProfileModel>>;

	/**
	 * Impersona un utente
	 * @param id - ID dell'utente da impersonare
	 * @returns Promise con FetchResponse contenente string (JWT token dell'utente impersonificato)
	 */
	impersonateUser(id: string): Promise<FetchResponse<string>>;

	/**
	 * Genera Excel per users
	 * @param query - Query string per il filtro
	 * @returns Promise con FetchResponse contenente Blob Excel
	 */
	generateExcel(query: string): Promise<FetchResponse<Blob>>;
}

/**
 * Implementazione degli endpoints per User
 * Mantiene esattamente gli stessi nomi dei metodi e URL del JavaScript originale
 */
const userEndpoints: UserEndpoints = {
	getUsers(query: string): Promise<FetchResponse<ODataResponse<ApplicationUser>>> {
		return api.get(`/odata/v1/Users${query}`);
	},

	getUser(id: string): Promise<FetchResponse<ApplicationUser>> {
		return api.get(`/odata/v1/Users/${id}?$expand=userRoles($expand=role)`);
	},

	updateUser(id: string, payload: ApplicationUser): Promise<FetchResponse<ApplicationUser>> {
		return api.put(`/api/v1/Users/${id}`, payload);
	},

	postUser(payload: ApplicationUser): Promise<FetchResponse<ApplicationUser>> {
		return api.post('/api/v1/Users', payload);
	},

	deleteUser(id: string): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/Users/${id}`);
	},

	addRoleToUser(id: string, roleId: string): Promise<FetchResponse<void>> {
		return api.put(`/api/v1/Users/${id}/actions/addRole/${roleId}`);
	},

	removeRoleFromUser(id: string, roleId: string): Promise<FetchResponse<void>> {
		return api.put(`/api/v1/Users/${id}/actions/removeRole/${roleId}`);
	},

	toggleEnablingStatus(id: string): Promise<FetchResponse<void>> {
		return api.put(`/api/v1/Users/${id}/actions/toggleEnablingStatus`);
	},

	getRoleByUserId(id: string): Promise<FetchResponse<string>> {
		return api.get(`/api/v1/Users/${id}/GetRoleByUserId`);
	},

	resetPassword(id: string, newPassword: string): Promise<FetchResponse<void>> {
		return api.post(`/api/v1/Users/${id}/ResetPassword`, null, { params: { newPassword } });
	},

	getProfile(): Promise<FetchResponse<ProfileModel>> {
		return api.get('/api/v1/Users/actions/getProfile');
	},

	updateProfile(payload: ProfileModel): Promise<FetchResponse<ProfileModel>> {
		return api.put('/api/v1/Users/actions/updateProfile', payload);
	},

	impersonateUser(id: string): Promise<FetchResponse<string>> {
		return api.get(`/api/v1/Users/ImpersonateUser/${id}`);
	},

	generateExcel(query: string): Promise<FetchResponse<Blob>> {
		return api.get(`/api/v1/Users/actions/generateExcel/${query}`, {
			responseType: 'blob',
		});
	},
};

export default userEndpoints;
